/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { JournalVoucherSystemLedgerInfo } from "./info";
import { JournalVoucherSystemLedgerService } from "services/journal-voucher-system-ledger";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

const initialMonthValues = () => ({
  month1: 0, month2: 0, month3: 0, month4: 0,
  month5: 0, month6: 0, month7: 0, month8: 0,
  month9: 0, month10: 0, month11: 0, month12: 0,
  total: 0,
});

const initialValues = {
  unit: "",
  address: "",
  accountCode: "",
  year: today.getFullYear().toString(),
  openingDebit: 0,
  openingCredit: 0,
  details: [
    { offsetAccount: "", ...initialMonthValues() }
  ],
  debitOccurence: initialMonthValues(),
  creditOccurence: initialMonthValues(),
  closingDebit: initialMonthValues(),
  closingCredit: initialMonthValues(),
  reportDate: "",
  ledgerClerk: "",
  chiefAccountant: "",
  legalRepresentative: "",
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
};

function JournalVoucherSystemLedgerDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [formInitials, setFormInitials] = useState(initialValues);

  useEffect(() => {
    (async () => {
      try {
        if (id) {
          const response = await JournalVoucherSystemLedgerService.getById(id);
          const apiData = response.data || response;

          let [sDay, sMonth, sYear] = [initialValues.signingDay, initialValues.signingMonth, initialValues.signingYear];
          if (apiData.reportDate?.includes("/")) {
            const pts = apiData.reportDate.split("/");
            if (pts.length === 3) [sDay, sMonth, sYear] = pts;
          }

          setFormInitials({
            ...apiData,
            signingDay: sDay,
            signingMonth: sMonth,
            signingYear: sYear,
          });
        } else {
          setFormInitials(initialValues);
        }
      } catch (e) {
        console.error("Load failed:", e);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const handleBack = () => navigation("/templates/journal-voucher-system-ledger");

  const num = (v) => (v !== undefined && v !== "" && !isNaN(v) ? Number(v) : 0);

  const mapMonthValues = (mv) => ({
    month1: num(mv.month1), month2: num(mv.month2), month3: num(mv.month3), month4: num(mv.month4),
    month5: num(mv.month5), month6: num(mv.month6), month7: num(mv.month7), month8: num(mv.month8),
    month9: num(mv.month9), month10: num(mv.month10), month11: num(mv.month11), month12: num(mv.month12),
    total: num(mv.total),
  });

  const submitForm = async (fValues) => {
    try {
      const payload = {
        unit: fValues.unit,
        address: fValues.address,
        accountCode: fValues.accountCode,
        year: fValues.year,
        openingDebit: num(fValues.openingDebit),
        openingCredit: num(fValues.openingCredit),
        details: (fValues.details || []).map(d => ({
          offsetAccount: d.offsetAccount,
          ...mapMonthValues(d),
        })),
        debitOccurence: mapMonthValues(fValues.debitOccurence || {}),
        creditOccurence: mapMonthValues(fValues.creditOccurence || {}),
        closingDebit: mapMonthValues(fValues.closingDebit || {}),
        closingCredit: mapMonthValues(fValues.closingCredit || {}),
        ledgerClerk: fValues.ledgerClerk,
        chiefAccountant: fValues.chiefAccountant,
        legalRepresentative: fValues.legalRepresentative,
        reportDate: `${fValues.signingDay.padStart(2, "0")}/${fValues.signingMonth.padStart(2, "0")}/${fValues.signingYear}`,
      };

      if (!id) {
        await JournalVoucherSystemLedgerService.create(payload);
        toast.success("Tạo sổ cái thành công!");
      } else {
        await JournalVoucherSystemLedgerService.update(id, payload);
        toast.success("Cập nhật thành công!");
      }
      navigation("/templates/journal-voucher-system-ledger");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Lưu thất bại!");
    }
  };

  const handlePrint = (pValues) => {
    const html = getPrintTemplate(pValues);
    const win = window.open("", "_blank");
    win.document.open();
    win.document.write(`<html><head><title>Print S05-DN</title>
      <style>@media print{@page{size:A4 landscape;margin:1cm}}body{margin:0}</style>
      </head><body onload="setTimeout(()=>[window.print(),window.close()], 500)">${html}</body></html>`);
    win.document.close();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Formik initialValues={formInitials} enableReinitialize onSubmit={submitForm}>
              {({ values, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card>
                    <SoftBox p={0}>
                      <JournalVoucherSystemLedgerInfo formData={{ values, setFieldValue, containView }} />
                      <SoftBox p={3} display="flex" justifyContent="space-between" mt={2}>
                        <SoftButton variant="gradient" color="light" onClick={handleBack}>Trở về</SoftButton>
                        {containView ? (
                          <SoftButton variant="gradient" color="success" onClick={() => handlePrint(values)}>In Sổ Cái S05-DN</SoftButton>
                        ) : (
                          <SoftButton disabled={isSubmitting} type="submit" variant="gradient" color="dark">{id ? "Lưu" : "Tạo mới"}</SoftButton>
                        )}
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default JournalVoucherSystemLedgerDetail;
